import {
  CUSTOMER,
  FEEDBACK,
  FINANCIAL,
  PROMOTION,
  REPORTS,
  REQUEST,
  ROOT,
  TEAM_MEMBER,
  TRAVELER,
  TRIP,
  HOME,
  BANNER_CONFIG,
} from 'configs/routeNames';
import type { MenuProps } from 'antd';
import { useState, ReactElement, useEffect, useCallback } from 'react';
import { Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideMenuList } from './ducks/types';
import CustomerIcon from 'assets/icons/CustomerIcon';
import FeedBackIcon from 'assets/icons/FeedBackIcon';
import HomeIcon from 'assets/icons/HomeIcon';
import PromotionIcon from 'assets/icons/PromotionIcon';
import ReportsIcon from 'assets/icons/ReportsIcon';
import RequestIcon from 'assets/icons/RequestIcon';
import TravelAgentIcon from 'assets/icons/TravelAgentIcon';
import TravelersIcon from 'assets/icons/TravelersIcon';
import TripsIcon from 'assets/icons/TripsIcon';
import FinancialIcon from 'assets/icons/FinancialIcon';
import AdminIcon from 'assets/icons/admin.svg';
import { useQueryClient } from '@tanstack/react-query';
import { isDevMode, NOT_IMPLEMENTED_SIDEMENU } from 'configs/constants';
import { PictureFilled } from '@ant-design/icons';
import './sidebar.css';
import { usePermissions } from 'app/hooks/usePermissions';
import { PERMISSIONS_SUBJECTS } from 'app/modules/Auth/constants';

// Map of paths to their corresponding selector IDs
const pathToSelectorMap: Record<string, string> = {
  [CUSTOMER]: 'customer-selector',
  [TRIP]: 'new-trip-selector',
  [TRAVELER]: 'traveler-selector',
  [TEAM_MEMBER]: 'team-member-selector',
  [FEEDBACK]: 'feedback-selector',
  [HOME]: 'home-selector',
  admin: 'admin-selector',
  [BANNER_CONFIG]: 'banner-config-selector',
};

interface SideMenuProps {
  onMenuItemSelect: (menuName: string) => void;
  collapsed?: boolean;
}

const SideMenu = ({ onMenuItemSelect, collapsed = false }: SideMenuProps): ReactElement => {
  const [menuItems, setMenuItems] = useState<MenuProps['items']>();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [, selectedKeys] = pathname.split('/');
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { hasVisibility } = usePermissions();

  const sideMenuItemsList: SideMenuList = [
    {
      path: 'admin',
      icon: <img src={AdminIcon} alt="Admin" className="size-6" />,
      labelKey: t('sideMenu.admin'),
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.BANNERS), // add permissions with OR condition as per childrens to show the admin menu
      children: [
        {
          path: BANNER_CONFIG,
          icon: <PictureFilled className="size-6" />,
          hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.BANNERS),
          labelKey: t('sideMenu.banners'),
        },
      ],
    },
    {
      path: HOME,
      icon: <HomeIcon className="size-6 " />,
      hasPermission: true,
      labelKey: t('sideMenu.home'),
    },
    ...(isDevMode
      ? [
          {
            path: PROMOTION,
            icon: <PromotionIcon className="size-6" />,
            labelKey: t('sideMenu.promotions'),
          },
        ]
      : []),
    ...(isDevMode
      ? [
          {
            path: REQUEST,
            icon: <RequestIcon className="size-6" />,
            labelKey: t('sideMenu.request'),
          },
        ]
      : []),
    {
      path: TRIP,
      icon: <TripsIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.TRIPS),

      labelKey: t('sideMenu.trips'),
    },
    {
      path: CUSTOMER,
      icon: <CustomerIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.CUSTOMERS),
      labelKey: t('sideMenu.customers'),
    },
    {
      path: TRAVELER,
      icon: <TravelersIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.TRAVELERS),
      labelKey: t('sideMenu.travelers'),
    },
    {
      path: TEAM_MEMBER,
      icon: <TravelAgentIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.TRAVEL_AGENTS),
      labelKey: t('sideMenu.travelagents'),
      headerName: t('sideMenu.team'),
    },
    {
      path: FEEDBACK,
      icon: <FeedBackIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.FEEDBACK),
      labelKey: t('sideMenu.feedback'),
    },
    {
      path: REPORTS,
      icon: <ReportsIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.REPORTS),
      labelKey: t('sideMenu.reports'),
      headerName: t('sideMenu.reports'),
    },
    {
      path: FINANCIAL,
      icon: <FinancialIcon className="size-6" />,
      hasPermission: hasVisibility(PERMISSIONS_SUBJECTS.FINANCIALS),
      labelKey: t('sideMenu.financial'),
      headerName: t('sideMenu.financial'),
    },
  ];

  const createSidebarMenuItems = useCallback(
    (menuItemsList: SideMenuList): MenuProps['items'] => {
      return menuItemsList.map(({ path, icon, labelKey, children, hasPermission }) => {
        const url = `${ROOT}${path}`;
        const isNotImplemented = NOT_IMPLEMENTED_SIDEMENU.includes(path);
        const selectorId = pathToSelectorMap[path];

        if (!hasPermission) {
          return null;
        }

        return {
          key: url,
          icon,
          id: selectorId,
          label: collapsed ? labelKey : <div className={isNotImplemented ? 'text-gray-600' : ''}>{t(labelKey)}</div>,
          title: collapsed ? t(labelKey) : undefined, // Add tooltip for collapsed state
          ...(children ? { children: createSidebarMenuItems(children) } : {}),
          onClick: () => {
            // Handle navigation for menu items without children (leaf nodes)
            if (!children) {
              // Navigate to the URL using React Router
              navigate(url);
              onMenuItemSelect(labelKey);
              queryClient.setQueryData(['header-props'], {
                selectedMenuItem: labelKey,
              });
            }
            // For items with children, let Ant Design handle the expansion
          },
        };
      });
    },
    [i18n.language, queryClient, collapsed, t, navigate],
  );

  useEffect(() => {
    const items = createSidebarMenuItems(sideMenuItemsList);
    setMenuItems(items);
  }, [createSidebarMenuItems]);

  // Auto-expand admin submenu when on admin routes (only when not collapsed)
  useEffect(() => {
    if (pathname.includes(BANNER_CONFIG) && !collapsed) {
      setOpenKeys([`${ROOT}admin`]);
    }
  }, [pathname, collapsed]);

  const onOpenChange = (key: string[]) => {
    setOpenKeys(key);
  };

  return (
    <Menu
      className={`side-menu h6-regular h-[calc(100vh-186px)] lg:h-[calc(100vh-266px)] ${collapsed ? 'px-2' : 'px-5'}`}
      mode="inline"
      inlineCollapsed={collapsed}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      defaultSelectedKeys={[ROOT]}
      selectedKeys={[`${ROOT}${selectedKeys}`]}
      items={menuItems}
      expandIcon={collapsed ? null : undefined}
    />
  );
};
export default SideMenu;
